/**
 * src/plugins/finance/payroll/payroll-calculator.ts
 *
 * Multi-Jurisdiction Payroll Statutory Calculator
 * Computes EPF/KWSP, SOCSO, EIS, PCB (Malaysia) and NSSF, PAYE, LST (Uganda)
 */

import type {
  PayrollMemberProfile,
  CalculatedPayslip,
  StatutoryBreakdownItem,
} from './payroll-contracts.js';

export class PayrollCalculator {
  /**
   * Calculates payslip for a given employee and pay period.
   */
  public calculatePayslip(
    employee: PayrollMemberProfile,
    payrollRunId: string,
    periodKey: string,
    allowancesMinor = 0,
    voluntaryDeductionsMinor = 0
  ): CalculatedPayslip {
    const grossSalaryMinor = employee.baseSalaryMinor + allowancesMinor;
    const jurisdiction = employee.jurisdictionCode || 'GLOBAL';

    let breakdown: StatutoryBreakdownItem[] = [];

    if (jurisdiction === 'MY') {
      breakdown = this.calculateMalaysiaStatutory(grossSalaryMinor, employee);
    } else if (jurisdiction === 'UG') {
      breakdown = this.calculateUgandaStatutory(grossSalaryMinor, employee);
    }

    const employeeStatutoryTotalMinor = breakdown.reduce((sum, item) => sum + item.employeeMinor, 0);
    const employerStatutoryTotalMinor = breakdown.reduce((sum, item) => sum + item.employerMinor, 0);

    const netSalaryMinor = Math.max(0, grossSalaryMinor - employeeStatutoryTotalMinor - voluntaryDeductionsMinor);

    return {
      payslipId: `ps_${employee.employeeId}_${periodKey}`,
      payrollRunId,
      employeeId: employee.employeeId,
      employeeName: employee.legalFullName,
      periodKey,
      baseSalaryMinor: employee.baseSalaryMinor,
      allowancesMinor,
      grossSalaryMinor,
      voluntaryDeductionsMinor,
      employeeStatutoryTotalMinor,
      employerStatutoryTotalMinor,
      statutoryBreakdown: breakdown,
      netSalaryMinor,
      currency: employee.currency,
      paymentMethod: employee.paymentMethod,
      status: 'calculated',
      calculatedAt: Date.now(),
    };
  }

  /**
   * Malaysia Statutory Schemes:
   * 1. EPF / KWSP: Employee 11%, Employer 12% (salary > 5k) or 13% (salary <= 5k).
   * 2. SOCSO / PERKESO: Approx 0.5% Employee / 1.75% Employer (capped at RM 5,000 ceiling -> RM 60,000 minor).
   * 3. EIS / SIP: 0.2% Employee / 0.2% Employer (capped at RM 5,000 ceiling).
   * 4. PCB / MTD: Progressive monthly income tax deduction.
   */
  private calculateMalaysiaStatutory(
    grossMinor: number,
    employee: PayrollMemberProfile
  ): StatutoryBreakdownItem[] {
    const items: StatutoryBreakdownItem[] = [];
    const my = employee.malaysiaSettings || {};

    // 1. EPF / KWSP
    const epfEmpRate = (my.epfRateEmployeePercent ?? 11) / 100;
    const epfEmprRate = (my.epfRateEmployerPercent ?? (grossMinor <= 500000 ? 13 : 12)) / 100;
    const epfEmployee = Math.round(grossMinor * epfEmpRate);
    const epfEmployer = Math.round(grossMinor * epfEmprRate);
    items.push({
      code: 'MY_EPF',
      label: 'Employees Provident Fund (KWSP / EPF)',
      employeeMinor: epfEmployee,
      employerMinor: epfEmployer,
    });

    // 2. SOCSO (PERKESO)
    if (my.socsoCategory !== 'exempt') {
      const socsoCappedGross = Math.min(grossMinor, 500000); // capped at RM 5,000
      const socsoEmployee = Math.round(socsoCappedGross * 0.005);
      const socsoEmployer = Math.round(socsoCappedGross * 0.0175);
      items.push({
        code: 'MY_SOCSO',
        label: 'Social Security Organization (SOCSO / PERKESO)',
        employeeMinor: socsoEmployee,
        employerMinor: socsoEmployer,
      });
    }

    // 3. EIS (SIP)
    if (my.eisEnrolled !== false) {
      const eisCappedGross = Math.min(grossMinor, 500000);
      const eisEmployee = Math.round(eisCappedGross * 0.002);
      const eisEmployer = Math.round(eisCappedGross * 0.002);
      items.push({
        code: 'MY_EIS',
        label: 'Employment Insurance System (EIS / SIP)',
        employeeMinor: eisEmployee,
        employerMinor: eisEmployer,
      });
    }

    // 4. PCB (Monthly Tax Deduction)
    const pcbTax = this.estimateMalaysiaPcbTax(grossMinor - epfEmployee);
    if (pcbTax > 0) {
      items.push({
        code: 'MY_PCB',
        label: 'Monthly Tax Deduction (PCB / MTD)',
        employeeMinor: pcbTax,
        employerMinor: 0,
      });
    }

    return items;
  }

  private estimateMalaysiaPcbTax(taxableMonthlyGrossMinor: number): number {
    // Annualized taxable projection
    const annualMinor = taxableMonthlyGrossMinor * 12;
    if (annualMinor <= 3500000) return 0; // First RM 35k exempt after reliefs
    if (annualMinor <= 5000000) return Math.round(((annualMinor - 3500000) * 0.03) / 12);
    if (annualMinor <= 7000000) return Math.round((45000 + (annualMinor - 5000000) * 0.06) / 12);
    if (annualMinor <= 10000000) return Math.round((165000 + (annualMinor - 7000000) * 0.11) / 12);
    return Math.round((495000 + (annualMinor - 10000000) * 0.19) / 12);
  }

  /**
   * Uganda Statutory Schemes:
   * 1. NSSF: Employee 5%, Employer 10%.
   * 2. PAYE (Pay As You Earn): Tiered tax brackets (UGX 235k exempt, 10%, 20%, 30%, 40% on > 10M).
   * 3. LST (Local Service Tax): Standard local government levy.
   */
  private calculateUgandaStatutory(
    grossMinor: number,
    employee: PayrollMemberProfile
  ): StatutoryBreakdownItem[] {
    const items: StatutoryBreakdownItem[] = [];
    const ug = employee.ugandaSettings || {};

    // 1. NSSF (5% Employee / 10% Employer)
    const nssfEmpRate = (ug.nssfRateEmployeePercent ?? 5) / 100;
    const nssfEmprRate = (ug.nssfRateEmployerPercent ?? 10) / 100;
    const nssfEmployee = Math.round(grossMinor * nssfEmpRate);
    const nssfEmployer = Math.round(grossMinor * nssfEmprRate);
    items.push({
      code: 'UG_NSSF',
      label: 'National Social Security Fund (NSSF Uganda)',
      employeeMinor: nssfEmployee,
      employerMinor: nssfEmployer,
    });

    // 2. PAYE Tiered Brackets (UGX monthly thresholds in minor units: * 100)
    const payeTax = this.calculateUgandaPaye(grossMinor);
    if (payeTax > 0) {
      items.push({
        code: 'UG_PAYE',
        label: 'Pay As You Earn (URA PAYE)',
        employeeMinor: payeTax,
        employerMinor: 0,
      });
    }

    // 3. Local Service Tax (LST)
    if (ug.lstTierMinor && ug.lstTierMinor > 0) {
      items.push({
        code: 'UG_LST',
        label: 'Local Service Tax (LST)',
        employeeMinor: ug.lstTierMinor,
        employerMinor: 0,
      });
    }

    return items;
  }

  private calculateUgandaPaye(grossMinor: number): number {
    // Thresholds:
    // <= 235,000 UGX (23,500,000 minor): 0%
    // 235,001 - 335,000 UGX: 10%
    // 335,001 - 410,000 UGX: 20%
    // 410,001 - 10,000,000 UGX: 30%
    // > 10,000,000 UGX: 30% + 10% top tier
    if (grossMinor <= 23500000) return 0;

    let tax = 0;
    if (grossMinor > 23500000) {
      const tier1 = Math.min(grossMinor, 33500000) - 23500000;
      tax += Math.round(tier1 * 0.10);
    }
    if (grossMinor > 33500000) {
      const tier2 = Math.min(grossMinor, 41000000) - 33500000;
      tax += Math.round(tier2 * 0.20);
    }
    if (grossMinor > 41000000) {
      const tier3 = Math.min(grossMinor, 1000000000) - 41000000;
      tax += Math.round(tier3 * 0.30);
    }
    if (grossMinor > 1000000000) {
      const topTier = grossMinor - 1000000000;
      tax += Math.round(topTier * 0.10); // Additional 10% for ultra-high earners
    }

    return tax;
  }
}
